import React, { useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism";
import Mermaid from "./mermaid";
import { MarkdownSkeleton } from "./markdown-skeleton";
import { ExternalLink, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import PersonaWorkflowRenderer from "./persona-workflow-renderer";
import CitationRenderer from "./citation-renderer";
import { useTheme } from "@/context/theme-context";

interface MarkdownProps {
  content: string;
}

// Above this many characters we don't run the content through autoCorrectHtml +
// ReactMarkdown/rehypeRaw: raw-HTML markdown parsing of a multi-MB blob (e.g. a
// large uploaded .txt) is synchronous and freezes the tab. Such documents get a
// bounded plain-text preview instead. Tune conservatively — normal generated
// docs/FRDs are well under this.
// Exported so the preview-panel chrome can match its own "preview is truncated"
// state (e.g. disable the Open button) to this renderer's cutoff.
export const MARKDOWN_MAX_RENDER_KB = 200;
export const MARKDOWN_MAX_RENDER_CHARS = MARKDOWN_MAX_RENDER_KB * 1024;

// Separate component for code blocks to properly use hooks
const CodeBlock: React.FC<{
  language: string;
  codeContent: string;
}> = ({ language, codeContent }) => {
  const [copied, setCopied] = useState(false);
  const { theme } = useTheme();
  const isLightTheme = theme === "light";
  // BREEZEAI-1125 QA follow-up: force explicit black-on-light / white-on-dark
  // for the inline CodeBlock header so the language label and buttons are
  // always legible regardless of theme-token resolution order.
  const headerTextColor = isLightTheme ? "text-black" : "text-white";
  const isHtmlBlock = language === "html";
  const isGherkinBlock = language === "gherkin";

  const handleCopy = () => {
    navigator.clipboard.writeText(codeContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleOpenHtml = () => {
    try {
      const blob = new Blob([codeContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("HTML opened in new tab");
    } catch (error) {
      toast.error("Failed to open HTML preview");
    }
  };

  const handleDownloadGherkin = () => {
    try {
      const blob = new Blob([codeContent], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "acceptance-criteria.feature";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Feature file downloaded successfully");
    } catch (error) {
      toast.error("Failed to download feature file");
    }
  };

  return (
    <div className="my-2 rounded-md overflow-hidden text-sm">
      <div
        style={{ color: isLightTheme ? "#000000" : "#FFFFFF" }}
        className="bg-surface-dark px-4 py-1 text-sm flex justify-between items-center select-none"
      >
        <span className={`${headerTextColor} font-medium`}>{language}</span>
        <div className="flex gap-2">
          {isHtmlBlock && (
            <button
              onClick={handleOpenHtml}
              className={`${headerTextColor} hover:opacity-80 text-sm flex items-center gap-1`}
              title="Open HTML in new tab"
            >
              <ExternalLink className="w-3 h-3" />
              Open
            </button>
          )}
          {isGherkinBlock && (
            <button
              onClick={handleDownloadGherkin}
              className={`${headerTextColor} hover:opacity-80 text-sm flex items-center gap-1`}
              title="Download as .feature file"
            >
              <Download className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={handleCopy}
            className={`${headerTextColor} hover:opacity-80 text-sm`}
            title="Copy code"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>
      <SyntaxHighlighter
        language={language}
        style={tomorrow}
        className="!text-sm"
        customStyle={{ margin: 0, borderRadius: "0 0 0.375rem 0.375rem" }}
        showLineNumbers={true}
        wrapLines={true}
        wrapLongLines={true}
      >
        {codeContent}
      </SyntaxHighlighter>
    </div>
  );
};

// Function to detect and wrap complete HTML pages that are not in code fences
const autoCorrectHtml = (content: string): string => {
  // Check if content already has ```html blocks
  const hasHtmlCodeBlocks = /```html\s*\n[\s\S]*?\n```/gi.test(content);

  // If it already has HTML code blocks, don't modify
  if (hasHtmlCodeBlocks) {
    return content;
  }

  // Single comprehensive pattern to detect complete HTML documents
  const htmlDocumentPattern =
    /(?:<!DOCTYPE\s+html[\s\S]*?<\/html>)|(?:<html[\s\S]*?<\/html>)/gi;

  let modifiedContent = content;
  let match;
  const processedMatches = new Set<string>();

  // Reset the regex to start from beginning
  htmlDocumentPattern.lastIndex = 0;

  // Process all HTML document matches one by one
  while ((match = htmlDocumentPattern.exec(content)) !== null) {
    const htmlMatch = match[0];

    // Skip if we already processed this exact match
    if (processedMatches.has(htmlMatch)) {
      continue;
    }

    // Check if this match is already in a code block in the current modified content
    const matchIndex = modifiedContent.indexOf(htmlMatch);
    if (matchIndex === -1) {
      // Match might have been replaced already, skip
      continue;
    }

    const beforeMatch = modifiedContent.substring(0, matchIndex);

    // Count code block markers before the match
    const codeBlocksBefore = (beforeMatch.match(/```/g) || []).length;

    // If odd number of ``` before the match, it means we're inside a code block
    const isInCodeBlock = codeBlocksBefore % 2 !== 0;

    if (!isInCodeBlock) {
      // Wrap the HTML content
      const wrappedHtml = `\`\`\`html\n${htmlMatch.trim()}\n\`\`\``;
      modifiedContent = modifiedContent.replace(htmlMatch, wrappedHtml);
      processedMatches.add(htmlMatch);
    }
  }

  return modifiedContent;
};

const Markdown: React.FC<MarkdownProps> = ({ content }) => {
  if (!content || content === "processing") {
    return <MarkdownSkeleton />;
  }

  // Oversized document (e.g. a large uploaded .txt): rendering the whole blob
  // through autoCorrectHtml + rehypeRaw would block the main thread and blank
  // the tab. Show a bounded plain-text preview instead — the full content is
  // still stored/generated server-side and can be downloaded.
  if (content.length > MARKDOWN_MAX_RENDER_CHARS) {
    const sizeMb = (content.length / (1024 * 1024)).toFixed(1);
    return (
      <div className="h-full overflow-y-auto overflow-x-hidden mb-2 px-2 md:px-6 max-w-none break-words text-text-primary scroll-smooth">
        <div className="my-2 flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning/10 p-3">
          <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed text-text-primary">
            Preview truncated — this document is {sizeMb} MB. Showing the first{" "}
            {MARKDOWN_MAX_RENDER_KB} KB. Download the file to view its full
            contents.
          </p>
        </div>
        <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
          {content.slice(0, MARKDOWN_MAX_RENDER_CHARS)}
        </pre>
      </div>
    );
  }

  // Auto-correct HTML content
  const correctedContent = autoCorrectHtml(content);

  // BREEZEAI-785: scroll container ref so in-page Table-of-Contents anchor
  // links (e.g. the FRD's `[FR-001 — System](#fr-001--system)`) jump to their
  // target within this scrollable pane instead of navigating the whole SPA.
  const containerRef = useRef<HTMLDivElement>(null);

  // BREEZEAI-785: resolve a `#anchor` link to its target and scroll it into
  // view. Targets are the raw `<a id="...">` anchors the FRD template emits
  // (handled by rehype-raw), matched inside this renderer's own DOM subtree.
  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    const id = decodeURIComponent(href.slice(1));
    if (!id) return;
    const container = containerRef.current;
    const target =
      (container?.querySelector(`#${CSS.escape(id)}`) as HTMLElement | null) ||
      document.getElementById(id);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Define markdown components
  const MarkdownComponents: React.ComponentProps<
    typeof ReactMarkdown
  >["components"] = {
    p({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <p className="mb-1 text-base" {...props}>
          {children}
        </p>
      );
    },
    h1({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <h1 className="text-lg font-bold mt-3 mb-1" {...props}>
          {children}
        </h1>
      );
    },
    h2({ children, ...props }: { children?: React.ReactNode }) {
      // Special styling for ReAct headings
      if (children && typeof children === "string") {
        const text = children.toString();
        if (
          text.includes("Thought") ||
          text.includes("Action") ||
          text.includes("Observation") ||
          text.includes("Answer")
        ) {
          return (
            <h2
              className={`text-base font-bold mt-3 mb-2 p-1 rounded ${
                text.includes("Thought")
                  ? "bg-info/20 text-info"
                  : text.includes("Action")
                  ? "bg-success/20 text-success"
                  : text.includes("Observation")
                  ? "bg-warning/20 text-warning"
                  : text.includes("Answer")
                  ? "bg-primary/20 text-primary"
                  : "text-text-primary"
              }`}
              {...props}
            >
              {children}
            </h2>
          );
        }
      }
      return (
        <h2 className="text-base font-bold mt-2 mb-1" {...props}>
          {children}
        </h2>
      );
    },
    h3({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <h3 className="text-base font-semibold mt-2 mb-1" {...props}>
          {children}
        </h3>
      );
    },
    h4({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <h4 className="text-sm font-semibold mt-2 mb-1" {...props}>
          {children}
        </h4>
      );
    },
    ul({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <ul
          className="list-disc list-outside pl-5 mb-1 text-base"
          {...props}
        >
          {children}
        </ul>
      );
    },
    ol({ children, ...props }) {
      return (
        <ol
          className="list-decimal list-outside pl-5 mb-1 text-base"
          {...props}
        >
          {children}
        </ol>
      );
    },
    li({ children, ...props }) {
      return (
        <li className="mb-1 text-base leading-relaxed" {...props}>
          {children}
        </li>
      );
    },
    strong({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <strong className="font-bold" {...props}>
          {children}
        </strong>
      );
    },
    em({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <em className="italic" {...props}>
          {children}
        </em>
      );
    },
    a({
      children,
      href,
      ...props
    }: {
      children?: React.ReactNode;
      href?: string;
    }) {
      // BREEZEAI-785: the FRD template emits raw `<a id="...">` anchor targets
      // (no href). Render them as plain jump targets so the `id` (in props) is
      // preserved and TOC links can scroll to them.
      if (!href) {
        return <a {...props}>{children}</a>;
      }
      // BREEZEAI-785: in-page anchor links (TOC entries, citation superscripts
      // like `#src-1`) scroll within this pane instead of opening a new tab.
      if (href.startsWith("#")) {
        return (
          <a
            href={href}
            className="text-purple-400 hover:underline"
            onClick={(e) => handleAnchorClick(e, href)}
            {...props}
          >
            {children}
          </a>
        );
      }
      return (
        <a
          href={href}
          className="text-purple-400 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children}
        </a>
      );
    },
    blockquote({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <blockquote
          className="border-l-2 border-surface-border pl-2 text-muted italic my-2"
          {...props}
        >
          {children}
        </blockquote>
      );
    },
    table({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <div className="overflow-x-auto my-2">
          <table className="min-w-full text-sm border-collapse" {...props}>
            {children}
          </table>
        </div>
      );
    },
    thead({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <thead className="bg-surface-hover" {...props}>
          {children}
        </thead>
      );
    },
    tbody({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <tbody className="divide-y divide-surface-border" {...props}>
          {children}
        </tbody>
      );
    },
    tr({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <tr className="hover:bg-surface-hover" {...props}>
          {children}
        </tr>
      );
    },
    th({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <th
          className="px-2 py-1 text-left font-medium text-text-primary"
          {...props}
        >
          {children}
        </th>
      );
    },
    td({ children, ...props }: { children?: React.ReactNode }) {
      return (
        <td className="px-2 py-1 border-t border-surface-border" {...props}>
          {children}
        </td>
      );
    },
    code(props: {
      inline?: boolean;
      className?: string;
      children?: React.ReactNode;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: any; // Using any here as it's required for ReactMarkdown components
    }) {
      const { inline, className, children, ...otherProps } = props;
      const match = /language-(\w+)/.exec(className || "");
      const codeContent = children ? String(children).replace(/\n$/, "") : "";

      // Handle Mermaid diagrams
      if (!inline && match && match[1] === "mermaid") {
        return (
          <div className="my-6 bg-surface-hover rounded-md overflow-hidden">
            <Mermaid
              chart={codeContent}
              className="w-full max-w-full"
              zoomingEnabled={true}
            />
          </div>
        );
      }

      // Handle BreezeAI JSON blocks
      if (!inline && match && match[1] === "breezeaijson") {
        try {
          const jsonData = JSON.parse(codeContent);
          return (
            <div className="my-6">
              <PersonaWorkflowRenderer data={jsonData} />
            </div>
          );
        } catch (error) {
          console.error("Failed to parse BreezeAI JSON:", error);
          return (
            <div className="my-6 bg-error/10 border border-error/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-error rounded-full"></div>
                <div className="text-error font-semibold">
                  BreezeAI JSON Parse Error
                </div>
              </div>
              <div className="text-muted text-sm mb-3">
                Failed to parse JSON content. The original content is shown
                below:
              </div>
              <div className="bg-surface-hover border border-surface-border rounded p-3">
                <div className="text-xs text-muted mb-2 font-medium">
                  Original Content:
                </div>
                <pre className="text-xs text-text-primary whitespace-pre-wrap break-words">
                  {codeContent}
                </pre>
              </div>
              <details className="mt-3">
                <summary className="text-error cursor-pointer text-sm hover:text-error/80">
                  Show error details
                </summary>
                <pre className="mt-2 text-xs bg-surface-input p-2 rounded border border-surface-border text-muted overflow-x-auto">
                  {error instanceof Error ? error.message : String(error)}
                </pre>
              </details>
            </div>
          );
        }
      }

      // Handle BreezeAI Citations blocks
      if (!inline && match && match[1] === "breezeaicitations") {
        try {
          const citationData = JSON.parse(codeContent);
          // Ensure citationData is an array of strings
          if (
            Array.isArray(citationData) &&
            citationData.every((item) => typeof item === "string")
          ) {
            return (
              <div className="my-6">
                <CitationRenderer citations={citationData} />
              </div>
            );
          } else {
            throw new Error("Citations data must be an array of strings");
          }
        } catch (error) {
          console.error("Failed to parse BreezeAI Citations:", error);
          return (
            <div className="my-6 bg-error/10 border border-error/30 rounded-lg p-4">
              <div className="text-error font-semibold mb-2">
                Invalid BreezeAI Citations Format
              </div>
              <div className="text-muted text-sm">
                Expected an array of Neo4j node ID strings. Please check the
                format.
              </div>
              <details className="mt-2">
                <summary className="text-error cursor-pointer text-sm">
                  Show raw content
                </summary>
                <pre className="mt-2 text-xs bg-surface-hover p-2 rounded border border-surface-border text-text-primary overflow-x-auto">
                  {codeContent}
                </pre>
              </details>
            </div>
          );
        }
      }

      // Handle code blocks
      if (!inline && match) {
        return <CodeBlock language={match[1]} codeContent={codeContent} />;
      }

      // Handle inline code
      return (
        <code
          className={`${className} font-mono bg-surface-hover px-1.5 py-0.5 rounded text-primary text-sm`}
          {...otherProps}
        >
          {children}
        </code>
      );
    },
  };

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto overflow-x-hidden mb-2 px-2 md:px-6 max-w-none break-words text-inherit [&_*]:text-inherit scroll-smooth"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={MarkdownComponents}
      >
        {correctedContent}
      </ReactMarkdown>
    </div>
  );
};

export default Markdown;
